import React, { useEffect, useState, useRef } from "react";
import { WifiOff, RefreshCw, ServerOff, CloudOff, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getSnapshotMeta } from "@/lib/offline-cache";

/**
 * Connectivity surface — todos los toasts son transitorios (4 s).
 *   - "Sin conexión" al perder red.
 *   - "Conexión restaurada" al recuperarla.
 *   - "Sin conexión con Altiv" cuando el server no responde aunque haya wifi.
 *   - "Cambio encolado" al meter algo al outbox.
 *   - "N cambios sincronizados" al vaciarse el outbox.
 *
 * Por qué transitorios y no persistentes:
 *   Aunque "no silent staleness" es un principio Altiv, el pill fijo era
 *   ruido visual constante. Las señales persistentes del estado offline ya
 *   viven en otros lugares: items del nav grisados con candado
 *   (Sidebar / BottomNav), OfflineUnavailable en rutas sin snapshot, y
 *   OfflineSyncBadge en el header cuando hay cambios pendientes.
 *
 * Reachable desde cualquier lado via notifyNetworkError() / notifyServerDown()
 * / notifyQueued() / notifySynced() — axios y el outbox los llaman.
 */
let _setBannerState = null;
const ONLINE_TOAST_MS = 4000;

export function notifyNetworkError() {
  if (_setBannerState) _setBannerState({ kind: "offline-blip", ts: Date.now() });
}

/**
 * Server unreachable state — el dispositivo tiene wifi pero el backend de
 * Altiv no responde (timeout, 5xx persistente). Se queda persistente hasta
 * que `notifyServerOk()` lo limpie (cualquier respuesta exitosa lo aclara).
 */
export function notifyServerDown() {
  if (_setBannerState) _setBannerState({ kind: "server-down", ts: Date.now() });
}

export function notifyServerOk() {
  if (_setBannerState) _setBannerState(null);
}

/** Toast cuando un POST/PUT se encoló en el outbox offline (no fue al server).
 *  Sale 4 s y desaparece — pero el OfflineSyncBadge sigue mostrando el contador
 *  hasta que se sincronice de verdad. */
export function notifyQueued(summary) {
  if (_setBannerState) _setBannerState({ kind: "queued", summary, ts: Date.now() });
}

/** Toast cuando el outbox terminó de subir cambios al server tras recuperar
 *  la red. Cierra el ciclo: el usuario sabe que sus cambios offline ya quedaron
 *  guardados de verdad, no solo localmente. */
export function notifySynced(count) {
  if (_setBannerState) _setBannerState({ kind: "synced", count, ts: Date.now() });
}

export default function ConnectivityBanner() {
  const { t } = useTranslation();
  // `null` = nothing showing. `{kind: 'offline'}` = persistent pill (real
  // navigator.onLine = false). `{kind: 'online'}` = transient "restaurada"
  // toast. `{kind: 'offline-blip'}` = transient "Sin conexión" hint when a
  // request failed but navigator hasn't flipped yet.
  const [state, setState] = useState(() =>
    typeof navigator !== "undefined" && !navigator.onLine
      ? { kind: "offline", ts: Date.now() }
      : null
  );
  const [snapshotAt, setSnapshotAt] = useState(null);
  const hideTimerRef = useRef(null);

  // Refresh snapshot age while the pill is up — once on mount and every 60 s
  // so "hace 1h" eventually rolls over to "hace 2h" without a navigation.
  useEffect(() => {
    if (state?.kind !== "offline") return;
    let alive = true;
    const tick = async () => {
      try {
        const meta = await getSnapshotMeta();
        if (alive) setSnapshotAt(meta?.last_snapshot_at || null);
      } catch {
        if (alive) setSnapshotAt(null);
      }
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [state?.kind]);

  useEffect(() => {
    _setBannerState = (s) => {
      setState(s);
      clearTimeout(hideTimerRef.current);
      // Todos los estados son transitorios — el banner asoma 4 s y se va.
      // El usuario sigue teniendo señales persistentes del estado offline:
      //   - Items del nav grisados con candado (Sidebar / BottomNav).
      //   - OfflineUnavailable en rutas sin snapshot.
      //   - OfflineSyncBadge en el header cuando hay items en el outbox.
      // El pill fijo era ruido visual constante mientras el offline duraba.
      if (s) {
        hideTimerRef.current = setTimeout(() => setState(null), ONLINE_TOAST_MS);
      }
    };
    const onOnline = () => _setBannerState({ kind: "online", ts: Date.now() });
    const onOffline = () => _setBannerState({ kind: "offline", ts: Date.now() });
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // Initial offline state on mount also auto-hides after 4 s.
    if (state?.kind === "offline") {
      hideTimerRef.current = setTimeout(() => setState(null), ONLINE_TOAST_MS);
    }
    return () => {
      _setBannerState = null;
      clearTimeout(hideTimerRef.current);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // Mount-only: registramos listeners globales una sola vez. `state` se lee
    // en el initial-offline branch pero solo nos importa el valor inicial;
    // agregarlo causaria re-registrar listeners en cada toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server-down: ping en background cada 15 s para detectar recuperación
  // sin que el usuario tenga que tocar nada. Cualquier respuesta exitosa al
  // health-check limpia el estado.
  useEffect(() => {
    if (state?.kind !== "server-down") return;
    let alive = true;
    const ping = async () => {
      try {
        const res = await fetch("/api/health", { method: "GET", cache: "no-store" });
        if (alive && res.ok) setState(null);
      } catch {
        /* sigue caído, no hacemos nada */
      }
    };
    const interval = setInterval(ping, 15000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [state?.kind]);

  if (!state) return null;
  const isOnline = state.kind === "online";
  const isServerDown = state.kind === "server-down";
  const isQueued = state.kind === "queued";
  const isSynced = state.kind === "synced";
  const ageLabel = state.kind === "offline" && snapshotAt ? formatAge(snapshotAt, t) : null;

  const onRetryServer = async () => {
    try {
      const res = await fetch("/api/health", { method: "GET", cache: "no-store" });
      if (res.ok) setState(null);
    } catch {
      /* sigue caído */
    }
  };

  // Estilos: verde (online / synced), azul (queued), rojo (server-down), ámbar (sin wifi).
  const tone =
    isOnline || isSynced
      ? "bg-emerald-500/15 border-emerald-500/40"
      : isQueued
        ? "bg-sky-500/15 border-sky-500/40"
        : isServerDown
          ? "bg-red-500/15 border-red-500/40"
          : "bg-amber-500/15 border-amber-500/40";

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[55] w-full max-w-md px-3 pointer-events-none"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}
    >
      <div
        className={`pointer-events-auto rounded-xl px-3 py-2 flex items-center gap-2.5 shadow-lg backdrop-blur-sm border ${tone}`}
      >
        {isOnline && (
          <>
            <RefreshCw className="w-4 h-4 text-emerald-300 flex-shrink-0" />
            <span className="text-xs font-semibold text-emerald-100">{t("offline.restored")}</span>
          </>
        )}
        {isSynced && (
          <>
            <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0" />
            <span className="text-xs font-semibold text-emerald-100">
              {state.count > 1 ? t("synced.titleMany", { n: state.count }) : t("synced.titleOne")}
            </span>
          </>
        )}
        {isServerDown && (
          <>
            <ServerOff className="w-4 h-4 text-red-300 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-red-100">{t("serverDown.title")}</div>
              <div className="text-[11px] text-red-200/80 truncate">{t("serverDown.subtitle")}</div>
            </div>
            <button
              onClick={onRetryServer}
              className="text-[11px] font-semibold text-red-100 bg-red-500/25 hover:bg-red-500/35 border border-red-500/40 rounded-lg px-2 py-1 flex-shrink-0"
            >
              {t("serverDown.retry")}
            </button>
          </>
        )}
        {isQueued && (
          <>
            <CloudOff className="w-4 h-4 text-sky-300 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-sky-100">{t("queued.title")}</div>
              <div className="text-[11px] text-sky-200/80 truncate">
                {state.summary
                  ? t("queued.subtitleWithSummary", { summary: state.summary })
                  : t("queued.subtitle")}
              </div>
            </div>
          </>
        )}
        {!isOnline && !isSynced && !isServerDown && !isQueued && (
          <>
            <WifiOff className="w-4 h-4 text-amber-300 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-100">{t("offline.title")}</span>
            <span className="text-[11px] text-amber-200/80 truncate">
              {ageLabel ? t("offline.subtitleWithAge", { age: ageLabel }) : t("offline.subtitle")}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function formatAge(iso, t) {
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return null;
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 60) return t("offline.ageMinutes", { n: mins });
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return t("offline.ageHours", { n: hrs });
    const days = Math.round(hrs / 24);
    return t("offline.ageDays", { n: days });
  } catch {
    return null;
  }
}
